import { Component, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { StepperActivateEvent } from '@progress/kendo-angular-layout';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ProgramCategoryList } from '../model/programCategoryList';
import { ProgramCategoryService } from 'src/app/core/service/program-category.service';
import { sortBy } from 'lodash';
import { Group, User } from 'src/app/user/model/user';
import { UserService } from 'src/app/core/service/user.service';
import { GroupService } from 'src/app/core/service/group.service';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { ChannelService } from 'src/app/core/service/channel.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { Channel } from 'src/app/channel/model/channel';
import { CaseService } from 'src/app/core/service/case.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { ComboBoxComponent, MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { ProgramService } from 'src/app/core/service/program.service';
import { Router } from '@angular/router';
import moment from 'moment';
import { Program } from '../model/program';
import { Guid } from 'guid-typescript';
import { FlowableService } from 'src/app/core/service/flowable.service';
import { NGXLogger } from 'ngx-logger';
import { ChannelCategoryService } from 'src/app/core/service/channel-category.service';

@Component({
  standalone: false,
  selector: 'app-program-wizard',
  templateUrl: './program-wizard.component.html',
  styleUrls: ['./program-wizard.component.scss']
})
export class ProgramWizardComponent
{
  @ViewChild('programTemplateControl') public programTemplateControl: ComboBoxComponent;
  @ViewChild('programNameControl') public programNameControl: ElementRef;
  @ViewChild('programCategoryControl') public programCategoryControl: ComboBoxComponent;
  @ViewChild('programLeadControl') public programLeadControl: ComboBoxComponent;
  @ViewChild('programGroupsControl') public programGroupsControl: MultiSelectComponent;
  @ViewChild('programDescriptionControl') public programDescriptionControl: ElementRef;
  @ViewChild('channelTemplateControl') public channelTemplateControl: ComboBoxComponent;
  @ViewChild('channelGroupsControl') public channelGroupsControl: MultiSelectComponent;
  @ViewChild('channelStaffsControl') public channelStaffsControl: MultiSelectComponent;
  @ViewChild('channelCategoryControl') public channelCategoryControl: ComboBoxComponent;
  @ViewChild('caseManagerAssignmentControl') public caseManagerAssignmentControl: ComboBoxComponent;
  @ViewChild('caseSensitivitiesControl') public caseSensitivitiesControl: MultiSelectComponent;
  @ViewChild('privateControl') public privateControl: ElementRef;
  @ViewChild('dashboardEnrollmentControl') public dashboardEnrollmentControl: ElementRef;
  @ViewChild('limitedEnrollmentControl') public limitedEnrollmentControl: ElementRef;
  @ViewChild('limitedViewControl') public limitedViewControl: ElementRef;
  @ViewChild('channelGoalControl') public channelGoalControl: NumericTextBoxComponent;
  @ViewChild('referralControl') public referralControl: ElementRef;

  public saving = false;
  public wizardOpened = false;
  public currentStep: number;
  public wizardForm: any;
  public searchTerm: string;
  public programCategories: ProgramCategoryList[] = [];
  public programCategoriesFiltered: ProgramCategoryList[] = [];
  public programLeads: User[] = [];
  public programLeadsFiltered: User[] = [];
  public templateChannels: any[];
  public templateGroupedChannels: GroupResult[] = [];
  public templateGroupedChannelsFiltered: GroupResult[] = [];
  public allGroups: Group[] = [];
  public groupsFiltered: Group[] = [];
  public staffCandidates = [];
  public staffCandidatesFiltered: User[] = [];
  public channelUsers: User[] = [];
  public channelCategories: ProgramCategoryList[] = [];
  public channelCategoriesFiltered: ProgramCategoryList[] = [];
  public staff = [];
  public caseManagerAssignmentModes: Array<{ text: string; value: string }> = [];
  public allSensitivities = [];
  public sensitivitiesFiltered: Dictionary[] = [];
  public virtualSensitivity: any = { itemHeight: 30 };
  public programTemplates: any[] = [];
  public programTemplatesFiltered: any[] = [];
  public channelGoalMaxDays: number;

  public steps = [
    { labelKey: 'WIZARD-NEW-OR-TEMPLATE-TITLE', hintKey: 'WIZARD-NEW-OR-TEMPLATE-HINT' },
    { labelKey: 'WIZARD-PROGRAM-NAME-TITLE', hintKey: 'WIZARD-PROGRAM-NAME-HINT' },
    { labelKey: 'WIZARD-CATEGORY-NAME-TITLE', hintKey: 'WIZARD-CATEGORY-NAME-HINT' },
    { labelKey: 'WIZARD-PRIMARY-LEAD-TITLE', hintKey: 'WIZARD-PRIMARY-LEAD-HINT' },
    { labelKey: 'WIZARD-SECONDARY-LEAD-TITLE', hintKey: 'WIZARD-SECONDARY-LEAD-HINT' },
    { labelKey: 'WIZARD-PROGRAM-DESCRIPTION-TITLE', hintKey: 'WIZARD-PROGRAM-DESCRIPTION-HINT' },
    { labelKey: 'WIZARD-ADD-CHANNEL-TITLE', hintKey: 'WIZARD-ADD-CHANNEL-HINT' },
    { labelKey: 'WIZARD-ASSIGNED-STAFF-GROUP-TITLE', hintKey: 'WIZARD-ASSIGNED-STAFF-GROUP-HINT' },
    { labelKey: 'WIZARD-ASSIGNED-STAFF-TITLE', hintKey: 'WIZARD-ASSIGNED-STAFF-HINT' },
    { labelKey: 'WIZARD-CHANNEL-CATEGORY-TITLE', hintKey: 'WIZARD-CHANNEL-CATEGORY-HINT' },
    { labelKey: 'WIZARD-CASE-MANAGER-TITLE', hintKey: 'WIZARD-CASE-MANAGER-HINT' },
    { labelKey: 'WIZARD-SENSITIVITY-TITLE', hintKey: 'WIZARD-SENSITIVITY-HINT' },
    { labelKey: 'WIZARD-LIMITED-ENROLLMENT-TITLE', hintKey: 'WIZARD-LIMITED-ENROLLMENT-HINT' },
    { labelKey: 'WIZARD-LIMITED-VIEW-TITLE', hintKey: 'WIZARD-LIMITED-VIEW-HINT' },
    { labelKey: 'WIZARD-REFERRAL-TITLE', hintKey: 'WIZARD-REFERRAL-HINT' },
    { labelKey: 'WIZARD-PRIVATE-TITLE', hintKey: 'WIZARD-PRIVATE-HINT' },
    { labelKey: 'WIZARD-DASHBOARD-ENROLLMENT-TITLE', hintKey: 'WIZARD-DASHBOARD-ENROLLMENT-HINT' },
    { labelKey: 'WIZARD-OPERATIONAL-TARGETS-TITLE', hintKey: 'WIZARD-OPERATIONAL-TARGETS-HINT' },
    { labelKey: 'WIZARD-SAVE-CONFIRMATION-TITLE', hintKey: 'WIZARD-SAVE-CONFIRMATION-HINT' },
  ];

  private ruleSetText: string;
  private program: Program;

  constructor(
    private messageHandlerService: MessageHandlerService,
    private programCategoryService: ProgramCategoryService,
    private userService: UserService,
    private groupService: GroupService,
    private channelService: ChannelService,
    private caseService: CaseService,
    private channelCategoryService: ChannelCategoryService,
    private dictionaryService: DictionaryService,
    private programService: ProgramService,
    private flowableService: FlowableService,
    private settingService: SettingService,
    private logger: NGXLogger,
    private router: Router,
    private fb: UntypedFormBuilder,
    private translate: TranslateService) { }

  public get creationType(): UntypedFormControl
  {
    return this.wizardForm.get('creationType');
  }

  public get programTemplate(): UntypedFormControl
  {
    return this.wizardForm.get('programTemplate');
  }

  public get programName(): UntypedFormControl
  {
    return this.wizardForm.get('programName');
  }

  public get programCategory(): UntypedFormControl
  {
    return this.wizardForm.get('programCategory');
  }

  public get leader(): UntypedFormControl
  {
    return this.wizardForm.get('leader');
  }

  public get organization(): UntypedFormControl
  {
    return this.wizardForm.get('organization');
  }

  public get programGroups(): UntypedFormControl
  {
    return this.wizardForm.get('programGroups');
  }

  public get description(): UntypedFormControl
  {
    return this.wizardForm.get('description');
  }

  public get channelTemplate(): UntypedFormControl
  {
    return this.wizardForm.get('channelTemplate');
  }

  public get channelName(): UntypedFormControl
  {
    return this.wizardForm.get('channelName');
  }

  public get channelGroups(): UntypedFormControl
  {
    return this.wizardForm.get('channelGroups');
  }

  public get channelStaffs(): UntypedFormControl
  {
    return this.wizardForm.get('channelStaffs');
  }

  public get channelCategory(): UntypedFormControl
  {
    return this.wizardForm.get('channelCategory');
  }

  public get caseManagerAssignmentMode(): UntypedFormControl
  {
    return this.wizardForm.get('caseManagerAssignmentMode');
  }

  public get sensitivities(): UntypedFormControl
  {
    return this.wizardForm.get('sensitivities');
  }

  public get private(): UntypedFormControl
  {
    return this.wizardForm.get('private');
  }

  public get limitedEnrollment(): UntypedFormControl
  {
    return this.wizardForm.get('limitedEnrollment');
  }

  public get dashboardEnrollment(): UntypedFormControl
  {
    return this.wizardForm.get('dashboardEnrollment');
  }

  public get limitedView(): UntypedFormControl
  {
    return this.wizardForm.get('limitedView');
  }

  public get timeline(): UntypedFormControl
  {
    return this.wizardForm.get('timeline');
  }

  public get success(): UntypedFormControl
  {
    return this.wizardForm.get('success');
  }

  public get referral(): UntypedFormControl
  {
    return this.wizardForm.get('referral');
  }

  public get formValid(): boolean
  {
    return this.wizardForm.valid;
  }

  /**
   * Open the program/channel wizard modal
   */
  public execute(): void
  {
    this.buildForm();
    document.body.style.overflow = 'hidden';
    this.wizardOpened = true;
    this.setDefaultsAndFocusControl(this.currentStep);
  }

  /**
   * Event occurs when a step is activated. This is to prevent step change until the first step selection is performed.
   *
   * @param event Stepper active event
   */
  public activateStep(event: StepperActivateEvent)
  {
    // Prevent selection other steps until the first selection is performed.
    if (this.currentStep === 0 && event.index > 0 && !this.isStepValid(this.currentStep))
    {
      event.preventDefault();
      this.creationType.markAsTouched();  // Mark as touched to force the error message to show.
    }
  }

  /**
   * Provide the step hint based for each step.
   *
   * @param step Step index
   * @returns Returns a step translation key to show the hint text. If the program template is displayed, we will return a different key..
   */
  public getStepHintKey(step: number): string
  {
    if (this.steps[step].hintKey === 'WIZARD-PROGRAM-NAME-HINT' &&
      this.creationType && this.creationType.value === 'template')
    {
      return `${this.steps[step].hintKey}-WITH-TEMPLATE`;
    }
    else
    {
      return this.steps[step].hintKey;
    }
  }

  /**
   * Check if the step is valid. This would either show a check icon or exclamation mark.
   *
   * @param step Step index to check (0 based index but adding 1 to start first step as 1)
   * @returns Returns true if the required step is meet.
   */
  public isStepValid = (step: number): boolean =>
  {
    switch (step + 1)
    {
      case 1:
        return this.creationType.value;
      case 2:
        return this.programName.value;
      case 3:
        return this.programCategory.value;
      case 4:
        return this.leader.value;
      case 6:
        return this.description.value;
      case 7:
        return this.channelName.value;
      case 17:  // Dashboard enrollment
        if (this.private.value || (this.channelCategory.value && this.channelCategory.value.name.toLowerCase().includes('referral'))) 
        {
          return !this.dashboardEnrollment.value
        }
        else
        {
          return true;
        }
      case 18:
        return this.isGoalsRequired() ? (this.timeline.value && this.success.value) : true;
      case 5:   // Secondary Leads is optional
      case 8:   // Channel assigned staff groups is optional
      case 9:   // Channel assigned staffs is optional
      case 10:  // Channel category is is optional
      case 11:  // Case Assignment Mode is is optional
      case 12:  // Sensitivity Flags is optional
      case 13:  // Limited enrollment defaulted to false
      case 14:  // Limited View is defaulted to false
      case 15:  // Referral is defaulted to false
      case 16:  // Private is defaulted to false
      default:
        return true;
    }
  };

  /**
   * Check if next button is enable. This is to disable the NEXT button on the first page until the selection on the first tab is performed.
   *
   * @returns Returns true if the NEXT button is enabled.
   */
  public isNextEnabled(): boolean
  {
    return this.currentStep > 0 || this.isStepValid(this.currentStep);
  }

  /**
   * Event triggered when the step is changed.
   *
   * @param stepIndex New selected step index.
   */
  public currentStepChange(stepIndex: number)
  {
    this.currentStep = stepIndex;
    this.setDefaultsAndFocusControl(this.currentStep);
  }

  /**
   * Close the wizard
   */
  public close(): void
  {
    document.body.style.overflow = '';
    this.wizardOpened = false;
  }

  /**
   * Filters the program categories based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterProgramCategories(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.programCategoriesFiltered = sortBy(this.programCategories
      .filter(category => category.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Filters the program leads based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterProgramLeads(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.programLeadsFiltered = sortBy(this.programLeads
      .filter(leader => leader.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Filters the program groups based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterGroups(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.groupsFiltered = sortBy(this.allGroups
      .filter(group => group.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Filters the channel staff based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterStaffCandidates(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.staffCandidatesFiltered = this.staffCandidates
      .filter(staff => staff.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Filters the channel category based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterChannelCategories(filterValue: string): void
  {
    this.channelCategoriesFiltered = sortBy(this.channelCategories
      .filter(category => category.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Filters the sensitivities based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterSensitivities(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.sensitivitiesFiltered = this.allSensitivities
      .filter(sensitive => sensitive.displayName.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Filters the channel templates based on user input. This method is filtering on the sub items (Channels).
   * The group will be filtered out if there is no Channels for the filter value entered.
   *
   * @param filterValue Filter value entered
   */
  public filterChannelTemplates(filterValue: string): void
  {
    this.searchTerm = filterValue;
    const filtered = this.templateGroupedChannels
      .map((x: any) => ({
        aggregates: {},
        field: 'programName',
        items: x.items.filter((v: Channel) => v.name.toLowerCase().includes(filterValue.toLowerCase())),
        value: x.value
      }));
    this.templateGroupedChannelsFiltered = filtered.filter(x => x.items.length > 0);
  }

  /**
   * Filters the program templates based on user input.
   *
   * @param filterValue Filter value entered
   */
  public filterProgramTemplates(filterValue: string): void
  {
    this.searchTerm = filterValue;
    this.programTemplatesFiltered = this.programTemplates
      .filter(program => program.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemInactive(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled;
  }

  /**
   * Set the item enable/disable state for the staff dropdown.
   *
   * @param itemArgs Item object
   * @returns boolean
   */
  public staffItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Method to indicate if the program group is selected.
   * This is use to set the custom checkbox of the kendo-multiselect.
   *
   * @param filterItem Filter property name
   * @param itemValue Item value id
   */
  public isGroupSelected(filterItem: string, itemValue: any): boolean
  {
    return this[filterItem].value && this[filterItem].value.map((m: Group) => m.id).includes(itemValue);
  }

  /**
   * Channel Template selection changed.
   *
   * @param selectedChannelTemplate Selected Channel Template
   */
  public channelTemplateChange(selectedChannelTemplate: Channel): void
  {
    if (selectedChannelTemplate)
    {
      this.resetChannelProperties();
      // Get the Channel details (needed to get all channels information)
      this.channelName.setValue(selectedChannelTemplate.name);
      this.channelService.getChannel(this.channelTemplate.value.id).subscribe({
        next: (channel) =>
        {
          this.channelTemplate.setValue(channel);
          // Set the default to all subsequent controls
          for (let i = this.currentStep; i < this.steps.length; i++)
          {
            this.setDefaultsAndFocusControl(i);
          }
        }
      });
    }
  }

  /**
   * Channel Template selection changed.
   *
   * @param selectedChannelTemplate Selected Channel Template
   */
  public programTemplateChange(selectedProgramTemplate: Program): void
  {
    if (selectedProgramTemplate)
    {
      this.resetProgramProperties();
      this.programName.setValue(selectedProgramTemplate.name);
      // Set the default to all subsequent controls
      const maxProgramStepIndex = this.steps.findIndex(x => x.labelKey === 'WIZARD-PROGRAM-DESCRIPTION-TITLE') + 1;
      for (let i = this.currentStep; i < maxProgramStepIndex; i++)
      {
        this.setDefaultsAndFocusControl(i);
      }
    }
  }

  /**
   * This method set the available staff candidates for selection.
   */
  public setStaffCandidates(): void
  {
    const groups = this.channelGroups.value ? this.channelGroups.value : [];
    // Users from groups are removed from available selection.
    const groupUsers = this.getSelectedUsersFromGroups(groups, this.allGroups);
    this.staffCandidates = sortBy(this.channelUsers.filter(u =>
      !this.staff.map(m => m.id).includes(u.id) &&
      !groupUsers.map(m => m.id).includes(u.id)), ['name']);
    this.filterStaffCandidates('');
  }

  /**
   * Event occurs if previous step is clicked
   */
  public previous(): void
  {
    this.currentStep -= 1;
    this.setDefaultsAndFocusControl(this.currentStep);
  }

  /**
   * Event occurs if the next step is clicked
   */
  public next(): void
  {
    this.currentStep += 1;
    this.setDefaultsAndFocusControl(this.currentStep);
  }

  /**
   * Save event
   */
  public save(): void
  {
    // Check if all steps are valid
    let allStepsValid = true;
    for (let i = 0; i < this.steps.length - 1; i++) 
    {
      if (!this.isStepValid(i))
      {
        allStepsValid = false;
      }
    }

    if (!allStepsValid)
    {
      this.messageHandlerService.displayError('WIZARD-ERROR');
      return;
    }

    // Set the program object
    this.program = new Program();
    this.program.id = null;
    this.program.version = 0;
    this.program.active = true;
    this.program.isTemplate = false;
    this.program.name = this.programName.value;
    this.program.description = this.description.value;
    this.program.leader = this.leader.value ? this.leader.value : null;
    this.program.category = this.programCategory.value ? this.programCategory.value : null;
    this.program.organization = this.organization.value;
    this.program.groups = this.programGroups.value && this.programGroups.value.length > 0 ?
      this.programGroups.value.map((x: any) => x.id) : null;
    this.program.ruleSetText = this.ruleSetText;

    // Set metrics to copy from template
    this.program.metrics = [];
    if (this.programTemplate && this.programTemplate.value &&
      this.programTemplate.value.metrics && this.programTemplate.value.metrics.length > 0)
    {
      this.programTemplate.value.metrics.forEach((metric: any) =>
      {
        metric.id = null;   // Set id to force a new metric
        this.program.metrics.push(metric);
      });
    }

    // Set the channel object
    const channel: any = new Channel();
    channel.id = null;
    channel.name = this.channelName.value;
    channel.isPrivate = this.private.value;
    channel.isLimitedEnrollment = this.limitedEnrollment.value;
    channel.isLimitedView = this.limitedView.value;
    channel.isDashboardEnrollment = this.dashboardEnrollment.value;
    channel.isWizard = true;
    channel.comments = null;
    channel.status = 'Offline';
    channel.active = true;
    channel.version = 0;
    channel.channelCategory = this.channelCategory.value ? this.channelCategory.value : null;
    channel.caseManagerAssignmentMode = this.caseManagerAssignmentMode.value;
    channel.copyChannelId = (this.channelTemplate.value) ? this.channelTemplate.value.id : null;
    channel.sensitivities = this.sensitivities.value && this.sensitivities.value.length > 0 ?
      this.sensitivities.value.map((x: any) => x.id) : null;
    channel.groups = this.channelGroups.value && this.channelGroups.value.length > 0 ?
      this.channelGroups.value.map((x: any) => x.id) : null;
    channel.users = this.channelStaffs.value && this.channelStaffs.value.length > 0 ?
      this.channelStaffs.value.map((x: any) => x.id) : null;
    channel.isReferral = this.referral.value;

    // Set goals
    channel.goals = [];
    const newGoal: any = {};
    newGoal.id = null;
    newGoal.version = 0;
    newGoal.type = 'targetDaysCloseCase';
    newGoal.success = this.success.value;
    newGoal.timeline = moment.duration(this.timeline.value, 'days');
    channel.goals.push(newGoal);

    // Creating flowable business process
    channel.bpmKey = Guid.create().toString();
    const bpm = {
      name: this.channelService.buildBPMDefaultName(this.channelName.value),
      key: channel.bpmKey,
      description: this.channelName.value,
      modelType: 0
    };

    this.saving = true;
    this.flowableService.createProcess(bpm).subscribe({
      next: (newProcess) =>
      {
        // Set channels business process variables
        channel.bpmId = newProcess.id;
        channel.bpmKey = newProcess.key;
        channel.bpmName = newProcess.name;

        this.program.channels = [];
        this.program.channels.push(channel);

        this.saveProgram(newProcess);
      },
      error: (errorMessage) =>
      {
        this.messageHandlerService.displayError(errorMessage);
      }
    });
  }

  /**
   * Event to prevent some characters for the Channel name
   *
   * @param event Keyboard event
   */
  public validateKey(event: KeyboardEvent): void
  {
    TextProcessor.validateKey(event);
  }

  public goalsBlur(): void
  {
    this.timeline.updateValueAndValidity();
    this.success.updateValueAndValidity();
  }

  /**
   * Check if step is required
   *
   * @param step Current step
   * @returns True if the step is required
   */
  public isStepRequired(step: number): boolean
  {
    switch (step)
    {
      case 1:
      case 2:
      case 3:
      case 5:
      case 6:
        return true;
      default:
        return false;
    }
  }

  /**
   * Build wizard form and all lookup initializations
   */
  private buildForm(): void
  {
    this.currentStep = 0;

    // Set labels translation
    this.steps.forEach((s: any) =>
    {
      s.label = this.translate.instant(s.labelKey);
      s.isValid = this.isStepValid;
    });

    // Build wizard form for all steps
    this.wizardForm = this.fb.group({
      creationType: [{ value: null, disabled: false }, Validators.required],
      programTemplate: [{ value: null, disabled: false }],
      programName: [{ value: null, disabled: false }, Validators.required],
      programCategory: [{ value: null, disabled: false }, Validators.required],
      leader: [{ value: null, disabled: false }, Validators.required],
      organization: [{ value: null, disabled: false }],
      programGroups: [{ value: null, disabled: false }],
      description: [{ value: null, disabled: false }, Validators.required],
      channelTemplate: [{ value: null, disabled: false }],
      channelName: [{ value: null, disabled: false }, Validators.required],
      channelGroups: [{ value: null, disabled: false }],
      channelStaffs: [{ value: null, disabled: false }],
      channelCategory: [{ value: null, disabled: false }],
      caseManagerAssignmentMode: [{ value: null, disabled: false }],
      sensitivities: [{ value: null, disabled: false }],
      private: [{ value: false, disabled: false }, Validators.required],
      limitedEnrollment: [{ value: false, disabled: false }, Validators.required],
      limitedView: [{ value: false, disabled: false }, Validators.required],
      dashboardEnrollment: [{ value: false, disabled: false }, Validators.required],
      referral: [{ value: false, disabled: false }, Validators.required],
      timeline: [{ value: null, disabled: false }],
      success: [{ value: null, disabled: false }],
    });

    this.wizardForm.get('leader').valueChanges.subscribe({
      next: (leader: User) =>
      {
        this.organization.setValue(leader && leader.organization ? leader.organization : null);
      }
    });

    this.wizardForm.get('timeline').valueChanges.subscribe({ next: (() => { this.setGoalControlsRequiredState(); }) });
    this.wizardForm.get('success').valueChanges.subscribe({ next: () => { this.setGoalControlsRequiredState(); } });
    this.wizardForm.get('referral').valueChanges.subscribe({ next: (referralValue: boolean) => { this.setPrivateState(referralValue); } });

    this.wizardForm.get('creationType').valueChanges.subscribe({
      next: (creationType: string) =>
      {
        if (creationType === 'new')
        {
          this.programTemplate.setValue(null);
          this.resetProgramProperties();
        }
        this.programTemplate.setValidators(creationType === 'template' ? [Validators.required] : []);
        this.programTemplate.updateValueAndValidity();
      }
    });

    this.programCategoryService.getCachedProgramCategories().subscribe({
      next: data =>
      {
        this.programCategories = data;
        this.filterProgramCategories('');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });

    this.userService.getCachedUsers().subscribe({
      next: (user: User[]) =>
      {
        this.programLeads = user;
        this.filterProgramLeads('');

        this.channelUsers = user;
        this.staffCandidates = sortBy(this.channelUsers, ['name']);
        this.filterStaffCandidates('');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });

    this.groupService.getGroups().subscribe({
      next: (data) =>
      {
        this.allGroups = data;
        this.filterGroups('');
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    // Load all Channels from Program Templates and keep them available for new Channels
    this.channelService.getCacheMinimalChannels('?templateOnly=true').subscribe({
      next: (channelData) =>
      {
        this.templateChannels = channelData;
        this.templateChannels.forEach(d => { d.programName = d.program.name; });
        this.setGroupedTemplateChannels();
      }
    });

    // Get Channel Categories
    this.channelCategoryService.getCachedChannelCategories().subscribe({
      next: data =>
      {
        this.channelCategories = data;
        this.filterChannelCategories('');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });

    // Get sensitivities
    this.dictionaryService.getCachedSensitivities().subscribe({
      next: (data) =>
      {
        this.allSensitivities = data;
        this.filterSensitivities('');
      }
    });

    this.programService.getProgramsTemplates().subscribe({
      next: (templates) =>
      {
        this.programTemplates = templates;
        this.filterProgramTemplates('');
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        this.channelGoalMaxDays = sessionSetting.channelGoalMaxDays;
      }
    });

    this.translate.get('NEW-ENROLLMENT-CRITERIA-DEFAULT-NAME').subscribe({
      next: (value) =>
      {
        this.ruleSetText = value;
      }
    });

    this.caseManagerAssignmentModes = this.caseService.caseManagerAssignmentModes;
  }

  private setPrivateState(referralValue: boolean): void 
  {
    // When Referral is checked on a Channel set Private=true and disable it
    if (referralValue) 
    {
      this.private.disable();
      this.private.setValue(true);
    }
    else
    {
      this.private.enable();
      this.private.setValue(false);
    }
    this.private.updateValueAndValidity();
  }

  /**
   * Set grouped Channels for each Programs
   */
  private setGroupedTemplateChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.templateChannels, [{ field: 'programName' }]);
    this.templateGroupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.templateGroupedChannels = sortBy(groupedChannels, ['value']);
    this.filterChannelTemplates('');
  }

  /**
   * Get the users based on the selected group(s).
   *
   * @param groups Groups
   * @param allGroups All groups
   * @returns Array of users
   */
  private getSelectedUsersFromGroups(groups: Array<Group>, allGroups: Array<Group>): Array<User>
  {
    const staff = [];
    groups.forEach(g =>
    {
      const group = allGroups.find(x => x.id === g.id);
      if (group)
      {
        if (group.users)
        {
          group.users.forEach(u =>
          {
            const index = staff.findIndex(x => x.id === u.id);
            if (index === -1)
            {
              staff.push(u);
            }
          });
        }
      }
    });
    return staff;
  }

  /**
   * This method set fields defaults based of the selected program template (if any) and focus field for each steps.
   *
   * @param step Step index to set the default and focus for.
   *             We set the focus only if the control is visible, otherwise the focus is not performed.
   */
  private setDefaultsAndFocusControl(step: number): void
  {
    setTimeout(() =>
    {
      switch (step + 1)
      {
        case 2:
          if (this.creationType.value === 'template')
          {
            if (this.programTemplateControl) { this.programTemplateControl.focus(); }
          }
          else
          {
            if (this.programNameControl) { this.programNameControl.nativeElement.focus(); }
          }
          break;
        case 3:
          if (this.programTemplate.value?.category && this.programCategory.value === null)
          {
            this.programCategory.setValue(this.programTemplate.value.category);
          }
          if (this.programCategoryControl) { this.programCategoryControl.focus(); }
          break;
        case 4:
          if (this.programTemplate.value?.leader && this.leader.value === null)
          {
            const enabledLeader = this.programLeads.find(x => x.id === this.programTemplate.value.leader.id);
            if (enabledLeader)
            {
              this.leader.setValue(enabledLeader);
            }
          }
          if (this.programLeadControl) { this.programLeadControl.focus(); }
          break;
        case 5:
          if (this.programTemplate.value?.groups && this.programTemplate.value.groups.length > 0 &&
            this.programGroups.value === null)
          {
            const activeGroups = this.allGroups.filter(x => x.active && this.programTemplate.value.groups.includes(x.id));
            if (activeGroups && activeGroups.length > 0)
            {
              this.programGroups.setValue(activeGroups);
            }
          }
          if (this.programGroupsControl) { this.programGroupsControl.focus(); }
          break;
        case 6:
          if (this.programTemplate.value?.description && this.description.value === null)
          {
            this.description.setValue(this.programTemplate.value.description);
          }
          if (this.programDescriptionControl) { this.programDescriptionControl.nativeElement.focus(); }
          break;
        case 7:
          if (this.channelTemplateControl) { this.channelTemplateControl.focus(); }
          break;
        case 8:
          if (this.channelTemplate.value?.groups && this.channelTemplate.value.groups.length > 0 &&
            this.channelGroups.value === null)
          {
            const activeGroups = this.allGroups.filter(x => x.active && this.channelTemplate.value.groups.includes(x.id));
            if (activeGroups && activeGroups.length > 0)
            {
              this.channelGroups.setValue(activeGroups);
            }
          }
          if (this.channelGroupsControl) { this.channelGroupsControl.focus(); }
          break;
        case 9:
          if (this.channelTemplate.value?.users && this.channelTemplate.value.users.length > 0 &&
            this.channelStaffs.value === null)
          {
            const activeUsers = this.staffCandidates.filter(x => x.enabled && this.channelTemplate.value.users.includes(x.id));
            if (activeUsers && activeUsers.length > 0)
            {
              this.channelStaffs.setValue(activeUsers);
            }
          }
          if (this.channelStaffsControl) { this.channelStaffsControl.focus(); }
          break;
        case 10:
          if (this.channelTemplate.value?.channelCategory && this.channelCategory.value === null)
          {
            this.channelCategory.setValue(this.channelTemplate.value.channelCategory);
          }
          if (this.channelCategoryControl) { this.channelCategoryControl.focus(); }
          break;
        case 11:
          if (this.channelTemplate.value?.caseManagerAssignmentMode && this.caseManagerAssignmentMode.value === null)
          {
            this.caseManagerAssignmentMode.setValue(this.channelTemplate.value.caseManagerAssignmentMode);
          }
          if (this.caseManagerAssignmentControl) { this.caseManagerAssignmentControl.focus(); }
          break;
        case 12:
          if (this.channelTemplate.value?.sensitivities && this.sensitivities.value === null)
          {
            const sensitivities = this.allSensitivities.filter(x => this.channelTemplate.value.sensitivities.includes(x.id));
            if (sensitivities && sensitivities.length > 0)
            {
              this.sensitivities.setValue(sensitivities);
            }
          }
          if (this.caseSensitivitiesControl) { this.caseSensitivitiesControl.focus(); }
          break;
        case 13:
          if (this.channelTemplate.value?.limitedEnrollment)
          {
            this.limitedEnrollment.setValue(this.channelTemplate.value.limitedEnrollment);
          }
          if (this.limitedEnrollmentControl) { this.limitedEnrollmentControl.nativeElement.focus(); }
          break;
        case 14:
          if (this.channelTemplate.value?.limitedView)
          {
            this.limitedView.setValue(this.channelTemplate.value.limitedView);
          }
          if (this.limitedViewControl) { this.limitedViewControl.nativeElement.focus(); }
          break;
        case 15:
          if (this.channelTemplate.value?.referral)
          {
            this.referral.setValue(this.channelTemplate.value.referral);
          }
          if (this.referralControl) { this.referralControl.nativeElement.focus(); }
          break;
        case 16:
          if (this.channelTemplate.value?.private)
          {
            this.private.setValue(this.channelTemplate.value.private);
          }
          if (this.privateControl) { this.privateControl.nativeElement.focus(); }
          break;
        case 17:
          if (this.channelTemplate.value?.dashboardEnrollment)
          {
            this.dashboardEnrollment.setValue(this.channelTemplate.value.dashboardEnrollment);
          }
          if (this.dashboardEnrollmentControl) { this.dashboardEnrollmentControl.nativeElement.focus(); }
          break;
        case 18:
          if (this.channelTemplate.value?.goals && this.channelTemplate.value.goals.length === 1 &&
            this.timeline.value === null)
          {
            this.timeline.setValue(moment.duration(this.channelTemplate.value.goals[0].timeline).asDays());
            this.success.setValue(this.channelTemplate.value.goals[0].success);
          }
          if (this.channelGoalControl) { this.channelGoalControl.focus(); }
          break;
      }
    });
  }

  /**
   * Reset all properties related with Program Template selection
   */
  private resetProgramProperties(): void
  {
    this.programName.setValue(null);
    this.programCategory.setValue(null);
    this.leader.setValue(null);
    this.programGroups.setValue(null);
    this.description.setValue(null);
  }

  /**
   * Reset all properties related with Channel Template selection
   */
  private resetChannelProperties(): void
  {
    this.channelName.setValue(null);
    this.channelGroups.setValue(null);
    this.channelStaffs.setValue(null);
    this.channelCategory.setValue(null);
    this.caseManagerAssignmentMode.setValue(null);
    this.sensitivities.setValue(null);
    this.private.setValue(false);
    this.limitedEnrollment.setValue(false);
    this.limitedView.setValue(false);
    this.dashboardEnrollment.setValue(false);
    this.timeline.setValue(null);
    this.success.setValue(null);
  }

  /**
   * Save the new Program and Channel and navigate to the program after save.
   *
   * @param newProcess New flowable process
   */
  private saveProgram(newProcess: any): void
  {
    this.programService.saveProgram(this.program, true, null).subscribe({
      next: (program) =>
      {
        this.program.id = program.id;
        this.messageHandlerService.displaySuccess('PROGRAM-SAVED-SUCCESSFULLY');

        // If a template is selected, must update model for each created channels
        // Must get the original bpmId if Channel Template is used
        const templateBpmId = this.channelTemplate.value ? this.channelTemplate.value.bpmId : null;
        if (templateBpmId !== undefined && templateBpmId !== null)
        {
          // Copy Business Process from Template
          this.flowableService.getModelSource(templateBpmId).subscribe({
            next: (templateModel) =>
            {
              templateModel.lastUpdated = newProcess.lastUpdated;
              templateModel.key = newProcess.key;
              templateModel.name = newProcess.name;
              templateModel.modelId = newProcess.id;
              templateModel.model.modelId = newProcess.id;
              this.flowableService.updateModelSource(newProcess.id, templateModel).subscribe({
                complete: () =>
                {
                  this.saving = false;
                  this.close();
                  this.router.navigate([`/program-edit/${program.id}`]);
                },
                error: (error) =>
                {
                  this.logger.error(error);
                  this.messageHandlerService.displayDirectMessage('error', error.message);
                  this.close();
                }
              });
            },
            error: (error) =>
            {
              this.logger.error(error);
              this.messageHandlerService.displayDirectMessage('error', error.message);
              this.close();
            }
          });
        }
        else
        {
          this.close();
          this.router.navigate([`/program-edit/${program.id}`]);
        }
      },
      error: (error) =>
      {
        this.messageHandlerService.displayDirectMessage('error', error.message);
        this.close();
      }
    });
  }

  /**
   * Check if any of the goal field is selected, both are required.
   *
   * @returns Returns true if any of the goal fields is selected.
   */
  private isGoalsRequired(): boolean
  {
    return (this.timeline.value || this.success.value);
  }

  /**
   * Set goals fields required.
   */
  private setGoalControlsRequiredState(): void
  {
    const isRequired = this.isGoalsRequired();
    this.timeline.setValidators(isRequired ? [Validators.required] : []);
    this.success.setValidators(isRequired ? [Validators.required] : []);
  }
}
